import get from 'lodash/get';
import { hentSpillerliste } from '../services/hentSpillerliste';
import { read, utils } from 'xlsx';

// Dynamic group color palette (shared)
export const GROUP_COLOR_PALETTE = [
  '#e3f2fd', // blue
  '#fffde7', // yellow
  '#ffebee', // red
  '#e99696', // dark red
];

// Returns array of unique group keys from player list
export function getGroupsFromPlayers(players) {
  const groupSet = new Set(players.map(p => p.group).filter(Boolean));
  return Array.from(groupSet).sort();
}

// Returns group info with color for each group
export function getGroupColorInfo(players) {
  const groups = getGroupsFromPlayers(players);
  return groups.map((group, idx) => ({
    group,
    color: GROUP_COLOR_PALETTE[idx % GROUP_COLOR_PALETTE.length],
  }));
}

let cachedObjectList = null;
let cachedNameList = null;
const playerNameRegex = /^[a-zA-ZæøåÆØÅüÜ\s-.]+$/;

export function distributePlayers(players, nedreGrense2Lag, versjon) {
  if (!versjon) {
    return distributePlayersV1(players, nedreGrense2Lag);
  }
  
  return distributeBalanced(players, nedreGrense2Lag);
}

function distributeBalanced(players, nedreGrense2Lag) {
  // Shuffle players for randomness
  const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);
  // Assign group colors dynamically
  const groupColorInfo = getGroupColorInfo(shuffledPlayers);
  const groupColorMap = Object.fromEntries(groupColorInfo.map(info => [info.group, info.color]));
  // Ensure numeric level for all players and add groupColor
  const normalized = shuffledPlayers.map(player => ({
    ...player,
    level: Number.isFinite(Number(player.level)) ? Number(player.level) : 0,
    groupColor: groupColorMap[player.group] || '',
  }));

  // Decide number of teams
  const numberOfTeams = normalized.length <= nedreGrense2Lag ? 2 : 4;
  const teams = Array.from({ length: numberOfTeams }, () => []);

  // Helpers
  const teamSum = t => t.reduce((s, p) => s + p.level, 0);
  const teamGroupCount = (t, grp) => t.filter(p => p.group === grp).length;
  const teamGroupPositionCount = (t, grp, pos) =>
    t.filter(p => p.group === grp && (p.position || 'unknown') === pos).length;

  // Separate senior players (group >= 55) from others.
  // All seniors must be split by position regardless of level.
  const seniorPlayers = normalized.filter(p => Number(p.group) >= 55);
  const otherPlayers = normalized.filter(p => !(Number(p.group) >= 55));

  // Distribute senior players grouped by position.
  // Within each position group, assign each player to the team with the lowest
  // current level sum so that no two players with the same position end up on
  // the same team (as long as there are enough teams).
  const seniorsByPosition = {};
  for (const p of seniorPlayers) {
    const pos = p.position || 'unknown';
    if (!seniorsByPosition[pos]) seniorsByPosition[pos] = [];
    seniorsByPosition[pos].push(p);
  }
  for (const pos in seniorsByPosition) {
    // Sort by level descending so the best player is placed first
    const posPlayers = seniorsByPosition[pos].sort((a, b) => b.level - a.level);
    // Track which teams already have a senior at this position to avoid duplicates
    const usedTeams = new Set();
    for (const player of posPlayers) {
      const targetTeam = teams
        .map((t, i) => ({ i, size: t.length, sum: teamSum(t) }))
        .filter(({ i }) => !usedTeams.has(i))  // prefer teams without this position
        .sort((a, b) => a.size - b.size || a.sum - b.sum)[0]?.i
        ?? teams.map((t, i) => ({ i, size: t.length, sum: teamSum(t) })).sort((a, b) => a.size - b.size || a.sum - b.sum)[0].i;
      teams[targetTeam].push(player);
      usedTeams.add(targetTeam);
    }
  }

  // Sort remaining players by level descending
  const sorted = otherPlayers.sort((a, b) => b.level - a.level);

  // Group remaining players by age group, then by position within each group
  const byGroupPosition = {};
  for (const p of sorted) {
    const grp = p.group;
    const pos = p.position || 'unknown';
    if (!byGroupPosition[grp]) byGroupPosition[grp] = {};
    if (!byGroupPosition[grp][pos]) byGroupPosition[grp][pos] = [];
    byGroupPosition[grp][pos].push(p);
  }

  // Distribute using four-level priority:
  // 1. Fewest total players on the team (equal team sizes)
  // 2. Fewest players of this group+position on the team (even positions within group)
  // 3. Fewest players of this group overall (even group size)
  // 4. Lowest total level sum (balance skill)
  for (const grp in byGroupPosition) {
    for (const pos in byGroupPosition[grp]) {
      for (const player of byGroupPosition[grp][pos]) {
        const targetTeam = teams
          .map((t, i) => ({
            i,
            size: t.length,
            posCount: teamGroupPositionCount(t, grp, pos),
            groupCount: teamGroupCount(t, grp),
            sum: teamSum(t),
          }))
          .sort((a, b) => a.size - b.size || a.posCount - b.posCount || a.groupCount - b.groupCount || a.sum - b.sum)[0].i;
        teams[targetTeam].push(player);
      }
    }
  }

  // If teams are unequal in size (unavoidable when total players % numberOfTeams !== 0),
  // ensure the player with the highest group+level is on one of the larger teams.
  // If they're not already there, swap only with a player from the same group to preserve group balance.
  const sizes = teams.map(t => t.length);
  const maxSize = Math.max(...sizes);
  const minSize = Math.min(...sizes);
  if (maxSize > minSize) {
    let bestPlayer = null, bestTeamIdx = -1, bestPlayerIdx = -1;
    for (let ti = 0; ti < teams.length; ti++) {
      for (let pi = 0; pi < teams[ti].length; pi++) {
        const p = teams[ti][pi];
        if (Number(p.group) >= 55) continue;
        if (
          !bestPlayer ||
          Number(p.group) > Number(bestPlayer.group) ||
          (Number(p.group) === Number(bestPlayer.group) && p.level > bestPlayer.level)
        ) {
          bestPlayer = p; bestTeamIdx = ti; bestPlayerIdx = pi;
        }
      }
    }
    const largeTeamIdx = sizes.indexOf(maxSize);
    if (bestPlayer && bestTeamIdx !== largeTeamIdx) {
      // Find the weakest non-senior on the large team with same group to keep group counts stable
      let worstIdx = -1, worstPlayer = null;
      for (let pi = 0; pi < teams[largeTeamIdx].length; pi++) {
        const p = teams[largeTeamIdx][pi];
        if (Number(p.group) >= 55) continue;
        if (p.group !== bestPlayer.group) continue;
        if (
          !worstPlayer ||
          Number(p.group) < Number(worstPlayer.group) ||
          (Number(p.group) === Number(worstPlayer.group) && p.level < worstPlayer.level)
        ) {
          worstPlayer = p; worstIdx = pi;
        }
      }
      if (worstIdx !== -1) {
        teams[bestTeamIdx][bestPlayerIdx] = worstPlayer;
        teams[largeTeamIdx][worstIdx] = bestPlayer;
      }
    }
  }

  // Final pass: try to balance teams by total level via pairwise swaps.
  // Senior players are never swapped to preserve position constraints.
  let improved = true;
  let attempts = 0;
  while (improved && attempts < 20) {
    const sums = teams.map(teamSum);
    const maxSum = Math.max(...sums);
    const minSum = Math.min(...sums);
    const diff = maxSum - minSum;
    if (diff <= 3) break;
    const maxIdx = sums.indexOf(maxSum);
    const minIdx = sums.indexOf(minSum);
    let swapped = false;
    for (let i = 0; i < teams[maxIdx].length && !swapped; i++) {
      for (let j = 0; j < teams[minIdx].length && !swapped; j++) {
        const playerA = teams[maxIdx][i];
        const playerB = teams[minIdx][j];
        // Never swap senior players
        if (Number(playerA.group) >= 55 || Number(playerB.group) >= 55) continue;
        const newDiff = Math.abs(
          (maxSum - playerA.level + playerB.level) -
          (minSum - playerB.level + playerA.level)
        );
        if (newDiff < diff) {
          teams[maxIdx][i] = playerB;
          teams[minIdx][j] = playerA;
          swapped = true;
        }
      }
    }
    improved = swapped;
    attempts++;
  }

  // Shuffle teams for randomness
  teams.sort(() => Math.random() - 0.5);
  return teams;
}

function distributePlayersV1(players, nedreGrense2Lag) {
  const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);
  const numberOfTeams = players.length <= nedreGrense2Lag ? 2 : 4;

  const teams = Array.from({ length: numberOfTeams }, () => []);

  let playersByAgePositionLevel = {};

  shuffledPlayers.forEach(player => {
    playersByAgePositionLevel = {
      ...playersByAgePositionLevel,
      [player.position]: {
        ...playersByAgePositionLevel[player.position],
        [player.group]: {
          ...get(playersByAgePositionLevel, [player.position, player.group], {}),
          [player.level || 'default']: [
            ...get(playersByAgePositionLevel, [player.position, player.group, player.level || 'default'], []),
            player,
          ],
        },
      }
    };
  });

  let teamIndex = 0;
  Object.values(playersByAgePositionLevel).forEach(groups => {
    Object.values(groups).forEach(levels => {
      Object.values(levels).forEach(players => {
        players.forEach(player => {
          teams[teamIndex++].push(player);
          if (teamIndex >= teams.length) {
            teamIndex = 0;
          }
        });
      });
    });
  });

  return teams;
}

export const finnUgyldigeNavn = (text) => {
  // Del teksten basert på linjeskift
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== "");

  return lines.filter(line => !playerNameRegex.test(line.trim()));
};

export const konverterSpillerlisteTilJson = async (liste) => {
  try {
    if (!cachedNameList || !liste.every(name => cachedNameList.includes(name))) {
      cachedNameList = liste;
      cachedObjectList = await hentSpillerliste();
    }

    // Mapper navnene til objekter ved å sammenligne med objektlisten
    const mappedObjects = liste.map((name) => {
      const searchWords = name.trim().toLowerCase().split(' ');

      const exactMatch = cachedObjectList.find(obj =>
        searchWords.every(word => obj.name.toLowerCase().includes(word))
      );

      return exactMatch
        ? exactMatch
        : { name, error: `Ingen match på "${name}"` };
    });

    return mappedObjects;
  } catch (error) {
    console.error("Feil ved henting av objektliste:", error);
    return [];
  }
};

export const parseExcelFile = async (file) => {
  const data = await file.arrayBuffer();
  const workbook = read(data, { type: 'array' });
  const sheetName = workbook.SheetNames[1];
  const worksheet = workbook.Sheets[sheetName];
  const json = utils.sheet_to_json(worksheet, { header: 1 });

  const headerIndex = json.findIndex(row => row && row[0] === 'Status');
  if (headerIndex === -1) return [];
  
  return json
    .slice(headerIndex + 1)
    .filter(row => row && row[0] === 'Kommer')
    .map(row => row[1].trim())
    .filter(Boolean);
};
